/**
 * Utilidades para validación y formateo de RUT chileno
 */

/**
 * Limpia un RUT removiendo puntos, guiones y espacios
 * @param rut RUT a limpiar
 * @returns RUT limpio
 */
export function cleanRut(rut: string): string {
  if (!rut) return '';
  return rut.replace(/[.\s-]/g, '').toUpperCase();
}

/**
 * Extrae el número del RUT sin el dígito verificador
 * @param rut RUT completo (ej: 12345678-5)
 * @returns Número del RUT
 */
export function getRutNumber(rut: string): string {
  const cleaned = cleanRut(rut);
  return cleaned.slice(0, -1);
}

/**
 * Extrae el dígito verificador del RUT
 * @param rut RUT completo (ej: 12345678-5)
 * @returns Dígito verificador
 */
export function getRutVerifier(rut: string): string {
  const cleaned = cleanRut(rut);
  return cleaned.slice(-1);
}

/**
 * Calcula el dígito verificador de un RUT
 * @param rutNumber Número del RUT (sin dígito verificador)
 * @returns Dígito verificador calculado
 */
export function calculateVerifier(rutNumber: string): string {
  let sum = 0;
  let multiplier = 2;

  for (let i = rutNumber.length - 1; i >= 0; i--) {
    sum += parseInt(rutNumber[i], 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = sum % 11;
  const verifier = 11 - remainder;

  if (verifier === 11) return '0';
  if (verifier === 10) return 'K';
  return verifier.toString();
}

/**
 * Valida si un RUT chileno es válido
 * @param rut RUT a validar (puede estar formateado o no)
 * @returns true si el RUT es válido, false en caso contrario
 */
export function isValidRut(rut: string): boolean {
  if (!rut) return false;

  const cleaned = cleanRut(rut);
  
  // Verificar formato básico: debe tener al menos 2 caracteres (número + verificador)
  if (cleaned.length < 2) return false;

  const rutNumber = getRutNumber(cleaned);
  const verifier = getRutVerifier(cleaned).toUpperCase();

  // Verificar que el número sea numérico
  if (!/^\d+$/.test(rutNumber)) return false;

  // Verificar que el verificador sea numérico o 'K'
  if (!/^\d|K$/.test(verifier)) return false;

  // Calcular y comparar el verificador
  const calculatedVerifier = calculateVerifier(rutNumber).toUpperCase();

  return calculatedVerifier === verifier;
}

/**
 * Formatea un RUT al formato chileno estándar (ej: 12.345.678-5)
 * @param rut RUT a formatear
 * @returns RUT formateado
 */
export function formatRut(rut: string): string {
  if (!rut) return '';

  const cleaned = cleanRut(rut);
  if (cleaned.length < 2) return rut;

  const rutNumber = getRutNumber(cleaned);
  const verifier = getRutVerifier(cleaned);

  // Agregar puntos al número
  let formattedNumber = '';
  for (let i = 0; i < rutNumber.length; i++) {
    if (i > 0 && (rutNumber.length - i) % 3 === 0) {
      formattedNumber += '.';
    }
    formattedNumber += rutNumber[i];
  }

  return `${formattedNumber}-${verifier}`;
}

/**
 * Normaliza un RUT: lo limpia, valida y formatea
 * @param rut RUT a normalizar
 * @returns RUT normalizado o null si es inválido
 */
export function normalizeRut(rut: string): string | null {
  if (!rut) return null;
  
  const cleaned = cleanRut(rut);
  if (!isValidRut(cleaned)) return null;
  
  return formatRut(cleaned);
}
